import { createSlice } from "@reduxjs/toolkit";

import {
  addSystemPromptData,
  deleteSystemPromptData,
  fetchSystemPromptsData,
  updateSystemPromptData,
} from "../thunks/systemPromptsThunk";

import type { SystemPrompt } from "../../utils/types/systemPrompts";

interface SystemPromptsState {
  systemPromptsList: SystemPrompt[];
  loading: boolean;
  error: string | null;
  isDataFetched?: boolean;
}

const initialState: SystemPromptsState = {
  systemPromptsList: [],
  loading: false,
  error: null,
  isDataFetched: false,
};

export const systemPromptsSlice = createSlice({
  name: "systemPrompts",
  initialState,
  reducers: {
    resetDataFetched: (state) => {
      state.isDataFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemPromptsData.pending, (state) => {
        state.loading = true;
        state.isDataFetched = true;
        state.error = null;
      })
      .addCase(fetchSystemPromptsData.fulfilled, (state, action) => {
        state.systemPromptsList = action.payload || [];
        state.loading = false;
      })
      .addCase(fetchSystemPromptsData.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isDataFetched = false;
        state.loading = false;
      });

    builder
      .addCase(addSystemPromptData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addSystemPromptData.fulfilled, (state, action) => {
        state.loading = false;
        state.systemPromptsList = [
          action.payload,
          ...state.systemPromptsList,
        ];
      })
      .addCase(addSystemPromptData.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Unknown error. Please try again";
      });

    builder
      .addCase(updateSystemPromptData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSystemPromptData.fulfilled, (state, action) => {
        state.loading = false;
        state.systemPromptsList = state.systemPromptsList.map((item) =>
          item.id === action.payload.data.id
            ? { ...item, ...action.payload.data }
            : item,
        );
      })
      .addCase(updateSystemPromptData.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Unknown error. Please try again";
      });

    builder
      .addCase(deleteSystemPromptData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSystemPromptData.fulfilled, (state, action) => {
        state.loading = false;
        state.systemPromptsList = state.systemPromptsList.filter(
          (item) => item.id !== action.payload.id,
        );
      })
      .addCase(deleteSystemPromptData.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Unknown error. Please try again";
      });
  },
});

export const { resetDataFetched } = systemPromptsSlice.actions;
export default systemPromptsSlice.reducer;
